import type { ApiImageProps } from 'components/UI/Image';
import type {
  AreaItem,
  ArticleHeadDataItems,
  ArticleHeadFields,
  ArticleHeadSection,
  MarketingDataItem,
  MarketingFields,
  SectionDataItem,
  SectionDataItemFields,
  ThemenweltProps,
  TrackingFields,
} from 'types/cms';
import type { StorylineFaqType, StorylineMarketingEmbedType } from 'types/storylines';
import { getFilename, normalizeBaseUrl } from './image';
import { slugify } from './string';

const MAX_META_IMAGE_WIDTH = 1200;

export const isArticle = (item: AreaItem) => {
  return (
    item.type === 'storyline' ||
    item.type === 'staticpage' ||
    item.type === 'video' ||
    item.type === 'sponsored-article' ||
    item.type === 'personas' ||
    item.type === 'topic' ||
    item.type === 'author' ||
    item.type === 'gallery'
  );
};

const isSectionData = (item: AreaItem) => {
  return item?.type === 'sectionData';
};

export const getSectionDataByAttributeValue = (attributeValue: keyof SectionDataItemFields, data?: Array<AreaItem>) => {
  return (data ?? [])
    .flatMap((item) => item.items)
    .filter(isSectionData)
    .reduce((acc, item: SectionDataItem) => item.model?.fields?.[attributeValue] ?? acc, undefined);
};

export const getThemenweltFromSectionData = (data?: Array<AreaItem>): ThemenweltProps | undefined => {
  return (data ?? [])
    .flatMap((item) => item.items)
    .filter(isSectionData)
    .reduce((acc, item: SectionDataItem) => item.model?.themenwelt ?? acc, undefined);
};

export const getArticleSections = (data?: Array<AreaItem>): Array<ArticleHeadSection> => {
  return (data ?? [])
    .filter((areaItem: AreaItem) => areaItem.view == 'head')
    .flatMap((item) => item.items)
    .filter(isArticle)
    .reduce((acc, item: ArticleHeadDataItems) => [...acc, ...(item.model?.sections ?? [])], []);
};

export const getArticleHeadDataByAttribute = (attributeValue: keyof ArticleHeadFields, data?: Array<AreaItem>) => {
  return (data ?? [])
    .filter((areaItem: AreaItem) => areaItem.view == 'head')
    .flatMap((areaItem) => areaItem.items)
    .filter(isArticle)
    .map((item: ArticleHeadDataItems) => item.model.fields?.[attributeValue])
    .find((value) => value !== undefined);
};

export const createMetaImage = (image: ApiImageProps | undefined) => {
  if (!image) {
    return undefined;
  }

  const width = Math.min(image.originalWidth, MAX_META_IMAGE_WIDTH);
  const height = Math.round(width * 0.5625);

  return {
    url: `${normalizeBaseUrl(image.baseUrl)}c16_9/${width}/${slugify(getFilename(image.filename, image.fields?.alt?.value))}.webp`,
    width,
    height,
  };
};

export const getMetaImage = (_pagetype?: string, data?: Array<AreaItem>) => {
  return (data ?? [])
    .filter((areaItem: AreaItem) => areaItem.view == 'head')
    .flatMap((item) => item.items)
    .filter(isArticle)
    .reduce(
      (acc, item: ArticleHeadDataItems) => createMetaImage(item.model['social-image'] ?? item.model.image) ?? acc,
      {
        url: '/rtl_logo.png',
        width: 1200,
        height: 630,
      }
    );
};

export const getMarketingDataByAttribute = (attributeValue: keyof MarketingFields, data?: Array<AreaItem>) => {
  return (data ?? [])
    .filter((areaItem: AreaItem) => areaItem.template == 'marketing')
    .reduce((accu, item: MarketingDataItem) => item.model?.marketing?.[attributeValue] ?? accu, undefined);
};

export const getTrackingDataByAttribute = (attributeValue: keyof TrackingFields, data?: Array<AreaItem>) => {
  return (data ?? [])
    .filter((areaItem: AreaItem) => areaItem.template == 'analytics')
    .reduce((accu, item) => item.model?.tracking?.[attributeValue] ?? accu, undefined);
};

export const getAdTagsFromHead = (data?: Array<AreaItem>): Array<string> => {
  if (!Array.isArray(data)) return [];

  const names = data
    .filter((areaItem) => areaItem.view === 'head' && areaItem.template === 'article-detail')
    .flatMap((areaItem) => (Array.isArray(areaItem.items) ? areaItem.items : []))
    .flatMap((item) => {
      const tags = item?.model?.tags ?? item?.model?.fields?.tags;
      return Array.isArray(tags) ? tags : [];
    })
    .filter((tag: { name?: string; id?: string }) => typeof tag?.id === 'string' && tag.id.includes('tag:ads'))
    .map((tag: { name?: string; id?: string }) => (tag?.name ?? tag?.id ?? '').trim())
    .filter(Boolean);

  return Array.from(new Set(names));
};

export const isStoryline = (metaArea: Array<AreaItem>) => {
  const articleDetailHead = metaArea.find((areaItem: AreaItem) => areaItem.view === 'head');

  if (articleDetailHead) {
    return articleDetailHead.items[0].type === 'storyline';
  }
  return false;
};

export const findAffiliateItems = (obj: ReadonlyArray<AreaItem>): Array<StorylineMarketingEmbedType> => {
  if (!obj) return [];
  let found: Array<StorylineMarketingEmbedType> = [];
  if (Array.isArray(obj)) {
    for (const item of obj) {
      found = found.concat(findAffiliateItems(item));
    }
  } else if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
    if ('template' in obj && obj.template === 'storyline_affiliate' && 'items' in obj && Array.isArray(obj.items)) {
      found.push(obj.items?.find?.((item: StorylineMarketingEmbedType) => item.type === 'affiliate')?.model);
    }
    for (const value of Object.values(obj)) {
      found = found.concat(findAffiliateItems(value));
    }
  }
  return found;
};

export const findFaqItems = (obj: ReadonlyArray<AreaItem>): Array<StorylineFaqType> => {
  if (!obj) return [];
  let found: Array<StorylineFaqType> = [];
  if (Array.isArray(obj)) {
    for (const item of obj) {
      found = found.concat(findFaqItems(item));
    }
  } else if (typeof obj === 'object' && obj !== null && !Array.isArray(obj)) {
    if ('items' in obj && Array.isArray(obj.items)) {
      const faqItem = obj.items?.find?.((item: { model?: { type?: string } }) => item?.model?.type === 'faq')?.model;
      if (faqItem) {
        found.push(faqItem);
      }
    }
    for (const value of Object.values(obj)) {
      found = found.concat(findFaqItems(value));
    }
  }
  return found;
};
